import { DOCUMENT } from "@angular/common";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    Inject,
    Signal,
    ViewChild,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { filter, map } from "rxjs";

import { A11yOptions, LazyOptions } from "swiper/types";

import {
    BreakpointsConfiguration,
    CarouselComponent,
    ControlsInset,
} from "@hermes/aphrodite/carousel";
import {
    GridFacade,
    GridFilter,
    GridFilterGroup,
    MIN_HIGHLIGHTS_LENGTH,
} from "@hermes/states/grid-filters-state";
import { BreakpointService } from "@hermes/utils-generic/services/user-interface";

/**
 * This component is used to display highlights (shortcuts that contains a link to a filtered grid) on the grid page
 */
@Component({
    selector: "h-grid-highlight",
    templateUrl: "./grid-highlight.component.html",
    styleUrls: ["./grid-highlight.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridHighlightComponent implements AfterViewInit {
    @ViewChild("carousel")
    private carousel!: CarouselComponent;

    public readonly isMobileSignal: Signal<boolean>;

    public readonly highlightedFiltersSignal: Signal<GridFilter[]>;

    public nbSliderVisibles: number = 0;
    public highlightedFilterGroupsSignal: Signal<GridFilterGroup[]>;

    public minHighlightLength = MIN_HIGHLIGHTS_LENGTH;

    public lazyLoadingOptions: LazyOptions = {
        enabled: true,
    };

    public breakpontsConfiguration: BreakpointsConfiguration = {
        // when window width is >= 768px
        768: {
            slidesPerView: 4,
            slidesPerGroup: 1,
        },
    };

    public carouselA11yOptions: A11yOptions = {
        enabled: true,
        slideRole: "listitem",
    };
    public carouselControlsInset: ControlsInset = {
        right: "8px",
        left: "8px",
        top: "calc(50% - 15px)",
        bottom: "auto",
    };

    constructor(
        private breakpointService: BreakpointService,
        @Inject(DOCUMENT) private document: Document,
        private gridFacade: GridFacade,
    ) {
        this.isMobileSignal = toSignal(
            this.breakpointService
                .mediumBreakpointObserver()
                .pipe(map(({ matches }) => !matches)),
            { initialValue: false },
        );

        this.highlightedFiltersSignal = toSignal(
            this.gridFacade.gridHighlights$,
            { initialValue: [] },
        );

        this.highlightedFilterGroupsSignal = toSignal(
            this.gridFacade.gridHighlightsFilterGroups$.pipe(filter(Boolean)),
            { initialValue: [] },
        );
    }

    public ngAfterViewInit(): void {
        // Add role attribut to generated .swiper-wrapper for A11Y purpose (External library)
        const swiperElement = this.document
            .getElementsByClassName("swiper-wrapper")
            .item(0);
        swiperElement?.setAttribute("role", "list");
    }

    public onSlidersPerViewChangeByBreakpoint(slidersPerView: number): void {
        this.nbSliderVisibles = slidersPerView;
    }

    public trackByHighlightedItems(index: number, item: GridFilter): string {
        return `${index}_${item?.name}`;
    }

    public handleFocusItem(index: number) {
        if (this.carousel) {
            this.carousel.swiper.swiperRef.slideToLoop(index, 400);
        }
    }
}
